import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { TranscriptCaption } from 'features/voice-meeting/interfaces';
import {
  voiceSessionFinished,
  voiceSessionReset,
  voiceSessionTeamingLeaveReset,
} from 'features/voice-session/store';

export interface CaptionLine extends TranscriptCaption {
  receivedAtMs: number;
}

export type CaptionRequester =
  | 'localInterpreter'
  | 'remoteInterpreter'
  | 'deafEndpoint';

export type CaptionRequests = Record<CaptionRequester, boolean>;

export interface CallCaptionsState {
  lines: CaptionLine[];
  captionsEnabled: boolean;
  captionRequests: CaptionRequests;
  // Voice session ids we successfully called start-captions for (Redux-owned bookkeeping).
  startedSessionIds: string[];
  trimOffsets: Record<string, number>;
}

export const initialCaptionRequests: CaptionRequests = {
  localInterpreter: false,
  remoteInterpreter: false,
  deafEndpoint: false,
};

export const initialCallCaptionsState: CallCaptionsState = {
  lines: [],
  captionsEnabled: false,
  captionRequests: initialCaptionRequests,
  startedSessionIds: [],
  trimOffsets: {},
};

const handleVoiceSessionEndSync = (state: CallCaptionsState) => {
  state.lines = [];
  state.startedSessionIds = [];
  state.trimOffsets = {};
  // Keep captionsEnabled — user preference survives reconnect within the same call.
};

const ensureCaptionRequests = (state: CallCaptionsState) => {
  state.captionRequests ??= { ...initialCaptionRequests };
};

const callCaptionsSlice = createSlice({
  name: 'callCaptions',
  initialState: initialCallCaptionsState,
  reducers: {
    captionReceived: {
      reducer: (state, action: PayloadAction<CaptionLine>) => {
        const incomingCaption = action.payload;
        const existingCaptionIndex = state.lines.findIndex(
          (caption) => caption.resultId === incomingCaption.resultId
        );

        if (existingCaptionIndex >= 0) {
          state.lines[existingCaptionIndex] = {
            ...incomingCaption,
            receivedAtMs: state.lines[existingCaptionIndex].receivedAtMs,
          };
          return;
        }

        state.lines.push(incomingCaption);
      },
      prepare: (caption: TranscriptCaption) => ({
        payload: {
          ...caption,
          receivedAtMs: Date.now(),
        },
      }),
    },
    pruneLines: (state, action: PayloadAction<string[]>) => {
      const keepIds = new Set(action.payload);
      state.lines = state.lines.filter((line) => keepIds.has(line.resultId));

      for (const turnKey of Object.keys(state.trimOffsets ?? {})) {
        if (!keepIds.has(turnKey)) {
          delete state.trimOffsets[turnKey];
        }
      }
    },
    incrementCaptionTrim: (
      state,
      action: PayloadAction<{ turnKey: string; charCount: number }>
    ) => {
      const { turnKey, charCount } = action.payload;

      if (!state.trimOffsets) {
        state.trimOffsets = {};
      }

      state.trimOffsets[turnKey] =
        (state.trimOffsets[turnKey] ?? 0) + charCount;
    },
    clearCaptions: (state) => {
      state.lines = [];
      state.trimOffsets = {};
    },
    resetCallCaptions: () => initialCallCaptionsState,
    // Local UI preference for this call; also mirrors localInterpreter demand.
    // Distinct from resetCallCaptions on full call reset.
    setCaptionsEnabled: (state, action: PayloadAction<boolean>) => {
      ensureCaptionRequests(state);
      state.captionsEnabled = action.payload;
      state.captionRequests.localInterpreter = action.payload;
    },
    // Per-participant demand. Only localInterpreter stays synced with
    // captionsEnabled (remote/deaf requests must not flip local UI).
    setCaptionRequest: (
      state,
      action: PayloadAction<{ requester: CaptionRequester; enabled: boolean }>
    ) => {
      ensureCaptionRequests(state);
      const { requester, enabled } = action.payload;
      state.captionRequests[requester] = enabled;

      if (requester === 'localInterpreter') {
        state.captionsEnabled = enabled;
      }
    },
    markCaptionsSessionStarted: (state, action: PayloadAction<string>) => {
      if (!state.startedSessionIds.includes(action.payload)) {
        state.startedSessionIds.push(action.payload);
      }
    },
    unmarkCaptionsSessionStarted: (state, action: PayloadAction<string>) => {
      state.startedSessionIds = state.startedSessionIds.filter(
        (id) => id !== action.payload
      );
    },
    clearStartedCaptionsSessions: (state) => {
      state.startedSessionIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(voiceSessionFinished, handleVoiceSessionEndSync)
      .addCase(voiceSessionReset, handleVoiceSessionEndSync)
      .addCase(voiceSessionTeamingLeaveReset, handleVoiceSessionEndSync);
  },
});

export const {
  captionReceived,
  pruneLines,
  incrementCaptionTrim,
  clearCaptions,
  resetCallCaptions,
  setCaptionsEnabled,
  setCaptionRequest,
  markCaptionsSessionStarted,
  unmarkCaptionsSessionStarted,
  clearStartedCaptionsSessions,
} = callCaptionsSlice.actions;

export const callCaptionsReducer = callCaptionsSlice.reducer;
